import { Component, computed, input } from '@angular/core';
import { PressureTemperatureCellViewModel } from '../../models/autogenerated-piping';

/** One display column pairing US Customary and International System values. */
interface PttDisplayCell {
  /** Temperature in °F (US Customary). */
  fahrenheit: number;
  /** Pressure in psig (US Customary). */
  psig: number;
  /** Temperature in °C (International System). */
  celsius: number;
  /** Pressure in KPag (International System). */
  kPag: number;
  /** Whether an asterisk should be appended to the temperature value. */
  isAsteriskRequired: boolean;
}

/** One column of the compare table — current and previous formatted values plus a changed flag for each row. */
export interface PttCompareColumn {
  currentFahrenheit: string;
  prevFahrenheit: string;
  currentPsig: string;
  prevPsig: string;
  currentCelsius: string;
  prevCelsius: string;
  currentKPag: string;
  prevKPag: string;
  fahrenheitChanged: boolean;
  psigChanged: boolean;
  celsiusChanged: boolean;
  kPagChanged: boolean;
}

/**
 * Reusable compare table for Pressure & Temperature cell data.
 *
 * Renders a 4-row table (°F / psig / °C / KPag) where changed cells are highlighted
 * with the old value struck through in red and the new value in green.
 *
 * Columns are matched **positionally** — column `i` in the current table is compared to
 * column `i` in the previous table, not by temperature value. This mirrors the display
 * order of `app-pressure-temp-table`.
 *
 * Usage:
 * ```html
 * <app-ptt-compare-table
 *   [currentCells]="presTempTable.cells"
 *   [previousCells]="getPreviousPTT(tableIndex)?.cells">
 * </app-ptt-compare-table>
 * ```
 */
@Component({
  selector: 'app-ptt-compare-table',
  templateUrl: './ptt-compare-table.component.html',
  styleUrls: ['./ptt-compare-table.component.scss'],
  standalone: false
})
export class PttCompareTableComponent {
  /** Raw cell array from the current revision's PTT table. */
  currentCells = input<PressureTemperatureCellViewModel[] | undefined>(undefined);

  /** Raw cell array from the previous revision's PTT table. Pass `undefined` when no previous data is available. */
  previousCells = input<PressureTemperatureCellViewModel[] | undefined>(undefined);

  /**
   * Computed column comparison data.
   * Each entry covers one positional column; the result covers
   * `max(currentCols, previousCols)` columns.
   */
  compareCols = computed<PttCompareColumn[]>(() => {
    const currentDisplayCells: PttDisplayCell[] = this.computeDisplayCells(this.currentCells() || []);
    const previousDisplayCells: PttDisplayCell[] = this.computeDisplayCells(this.previousCells() || []);

    const colCount: number = Math.max(currentDisplayCells.length, previousDisplayCells.length);
    const result: PttCompareColumn[] = [];

    const fmtF = (c: PttDisplayCell | undefined): string => {
      if (!c) { return ''; }
      const asterisk: string = c.isAsteriskRequired ? '*' : '';
      return this.formatTemperature(c.fahrenheit) + asterisk;
    };
    const fmtPsig = (c: PttDisplayCell | undefined): string =>
      c ? Math.round(c.psig).toString() : '';
    const fmtC = (c: PttDisplayCell | undefined): string => {
      if (!c) { return ''; }
      const asterisk: string = c.isAsteriskRequired ? '*' : '';
      return this.formatTemperature(c.celsius) + asterisk;
    };
    const fmtKPag = (c: PttDisplayCell | undefined): string =>
      c ? Math.round(c.kPag).toString() : '';

    for (let i: number = 0; i < colCount; i++) {
      const curr: PttDisplayCell | undefined = currentDisplayCells[i];
      const prev: PttDisplayCell | undefined = previousDisplayCells[i];

      const currentFahrenheit: string = fmtF(curr);
      const prevFahrenheit: string = fmtF(prev);
      const currentPsig: string = fmtPsig(curr);
      const prevPsig: string = fmtPsig(prev);
      const currentCelsius: string = fmtC(curr);
      const prevCelsius: string = fmtC(prev);
      const currentKPag: string = fmtKPag(curr);
      const prevKPag: string = fmtKPag(prev);

      result.push({
        currentFahrenheit,
        prevFahrenheit,
        currentPsig,
        prevPsig,
        currentCelsius,
        prevCelsius,
        currentKPag,
        prevKPag,
        // Compare the formatted display strings — what the user sees determines whether a cell is highlighted
        fahrenheitChanged: currentFahrenheit !== prevFahrenheit,
        psigChanged: currentPsig !== prevPsig,
        celsiusChanged: currentCelsius !== prevCelsius,
        kPagChanged: currentKPag !== prevKPag
      });
    }

    return result;
  });

  /**
   * Converts a raw `PressureTemperatureCellViewModel[]` into display cells (one per column),
   * pairing US Customary (°F/psig) and International System (°C/KPag) cells by sorted
   * temperature position. Mirrors the logic in `PressureTempTableComponent.getDisplayCells()`.
   */
  private computeDisplayCells(cells: PressureTemperatureCellViewModel[]): PttDisplayCell[] {
    if (!cells || cells.length === 0) { return []; }

    // PTT tables store paired US Customary (°F/psig) and International (°C/KPag) cells.
    // Pair them by sorted-temperature position, mirroring PressureTempTableComponent.getDisplayCells().
    const usCustomaryCells: PressureTemperatureCellViewModel[] = cells
      .filter(c =>
        c.isUSCustomary === true ||
        (c.temperatureUnit?.toLowerCase() === 'f' && c.pressureUnit?.toLowerCase() === 'psig')
      )
      .sort((a, b) => (a.temperature ?? 0) - (b.temperature ?? 0));

    const intlCells: PressureTemperatureCellViewModel[] = cells
      .filter(c =>
        c.isInternationalSystem === true ||
        (c.temperatureUnit?.toLowerCase() === 'c' && c.pressureUnit?.toLowerCase() === 'kpag')
      )
      .sort((a, b) => (a.temperature ?? 0) - (b.temperature ?? 0));

    const colCount: number = Math.max(usCustomaryCells.length, intlCells.length);
    if (colCount === 0) { return []; }

    const result: PttDisplayCell[] = [];
    for (let i: number = 0; i < colCount; i++) {
      const usCell: PressureTemperatureCellViewModel | undefined = usCustomaryCells[i];
      const intCell: PressureTemperatureCellViewModel | undefined = intlCells[i];
      result.push({
        fahrenheit: usCell?.temperature ?? 0,
        psig: usCell?.pressure ?? 0,
        celsius: intCell?.temperature ?? 0,
        kPag: intCell?.pressure ?? 0,
        // Prefer the US cell's asterisk flag; fall back to the international cell's
        isAsteriskRequired: (usCell ?? intCell)?.isAsteriskRequired ?? false
      });
    }

    return result;
  }

  /**
   * Formats a temperature value for display.
   * Rounds to at most one decimal place and drops the trailing `.0` for whole numbers —
   * identical to `PressureTempTableComponent.formatTemperature()`.
   */
  private formatTemperature(temp: number | undefined | null): string {
    if (temp === undefined || temp === null) { return ''; }
    const rounded: number = Math.round(temp * 10) / 10;
    return rounded === Math.floor(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  }
}
